package com.fantasytenis.web.theme

import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.rgb
import org.jetbrains.compose.web.css.rgba

/**
 * Fantasy Tennis color palette.
 * All colors used in the application should be defined here for consistency
 * and to enable future dark mode support.
 */
object Colors {
    // Primary - Tennis Green
    val Primary = rgb(0, 166, 81) // #00A651
    val PrimaryLight = rgb(76, 196, 133)
    val PrimaryDark = rgb(0, 128, 63)

    // Secondary - Championship Gold
    val Secondary = rgb(255, 215, 0) // #FFD700
    val SecondaryLight = rgb(255, 235, 102)
    val SecondaryDark = rgb(204, 172, 0)

    // Accent - Electric Blue
    val Accent = rgb(33, 150, 243) // #2196F3
    val AccentLight = rgb(100, 181, 246)
    val AccentDark = rgb(25, 118, 210)

    // Neutrals
    val White = rgb(255, 255, 255)
    val Black = rgb(0, 0, 0)
    val Gray50 = rgb(250, 250, 250)
    val Gray100 = rgb(245, 245, 245)
    val Gray200 = rgb(238, 238, 238)
    val Gray300 = rgb(224, 224, 224)
    val Gray400 = rgb(189, 189, 189)
    val Gray500 = rgb(158, 158, 158)
    val Gray600 = rgb(117, 117, 117)
    val Gray700 = rgb(97, 97, 97)
    val Gray800 = rgb(66, 66, 66)
    val Gray900 = rgb(33, 33, 33)

    // Semantic Colors
    val Success = rgb(76, 175, 80) // #4CAF50 Green
    val SuccessLight = rgb(232, 245, 233) // #E8F5E9
    val SuccessDark = rgb(56, 142, 60) // #388E3C
    val Warning = rgb(255, 152, 0) // #FF9800 Orange
    val WarningLight = rgb(255, 243, 224) // #FFF3E0
    val WarningDark = rgb(245, 124, 0) // #F57C00
    val Error = rgb(244, 67, 54) // #F44336 Red
    val ErrorLight = rgb(255, 235, 238) // #FFEBEE
    val ErrorDark = rgb(211, 47, 47) // #D32F2F
    val Info = rgb(33, 150, 243) // #2196F3 Blue
    val InfoLight = rgb(227, 242, 253) // #E3F2FD
    val InfoDark = rgb(25, 118, 210) // #1976D2

    // Live indicator
    val Live = rgb(244, 67, 54) // #F44336 Red - same as Error for visibility

    // Background
    val Background = rgb(250, 250, 250)
    val Surface = rgb(255, 255, 255)
    val SurfaceVariant = rgb(245, 245, 245)
    val SurfaceHover = rgb(248, 248, 248) // Slightly darker for hover

    // Text
    val TextPrimary = rgb(33, 33, 33)
    val TextSecondary = rgb(117, 117, 117)
    val TextDisabled = rgb(189, 189, 189)
    val TextOnPrimary = rgb(255, 255, 255)

    // Border
    val Border = rgb(224, 224, 224) // Gray300
    val BorderLight = rgb(240, 240, 240) // #F0F0F0

    // Contest Type Badge Colors
    object ContestType {
        val GPP = rgb(103, 58, 183) // #673AB7 Purple
        val HeadToHead = rgb(33, 150, 243) // #2196F3 Blue
        val FiftyFifty = rgb(76, 175, 80) // #4CAF50 Green
        val Multiplier = rgb(255, 152, 0) // #FF9800 Orange
        val Free = rgb(158, 158, 158) // #9E9E9E Gray
    }

    // Contest Status Badge Colors
    object ContestStatus {
        val UpcomingBackground = rgb(227, 242, 253) // #E3F2FD
        val UpcomingText = rgb(25, 118, 210) // #1976D2
        val LiveBackground = rgb(232, 245, 233) // #E8F5E9
        val LiveText = rgb(56, 142, 60) // #388E3C
        val CompletedBackground = rgb(245, 245, 245) // #F5F5F5
        val CompletedText = rgb(117, 117, 117) // #757575
        val CancelledBackground = rgb(255, 235, 238) // #FFEBEE
        val CancelledText = rgb(211, 47, 47) // #D32F2F
    }

    // Lineup Category Colors
    object Category {
        val Serve = rgb(37, 99, 235) // #2563eb Blue
        val Forehand = rgb(22, 163, 74) // #16a34a Green
        val Backhand = rgb(220, 38, 38) // #dc2626 Red
        val Volleys = rgb(147, 51, 234) // #9333ea Purple
        val Fighting = rgb(234, 88, 12) // #ea580c Orange
    }

    // Progress Bar Colors
    object Progress {
        val Normal = Primary
        val Warning = rgb(255, 152, 0) // #FF9800 Orange at 70%
        val Danger = rgb(244, 67, 54) // #F44336 Red at 90%
        val Background = rgb(224, 224, 224) // #E0E0E0
    }

    // Highlight Colors (for selected items, user entries, etc.)
    object Highlight {
        val Background = rgb(227, 242, 253) // #E3F2FD Light Blue
        val BackgroundHover = rgb(200, 230, 250) // Slightly darker for hover
        val Text = rgb(25, 118, 210) // #1976D2 Blue
    }

    // Medal Colors for Leaderboards
    object Medal {
        val GoldBackground = rgb(255, 215, 0) // #FFD700
        val GoldText = rgb(0, 0, 0)
        val SilverBackground = rgb(192, 192, 192) // #C0C0C0
        val SilverText = rgb(0, 0, 0)
        val BronzeBackground = rgb(205, 127, 50) // #CD7F32
        val BronzeText = rgb(255, 255, 255)
    }

    // Bracket Challenge Status Colors
    object BracketStatus {
        val UpcomingBackground = rgb(227, 242, 253) // #E3F2FD
        val UpcomingText = rgb(25, 118, 210) // #1976D2
        val LiveBackground = rgb(232, 245, 233) // #E8F5E9
        val LiveText = rgb(56, 142, 60) // #388E3C
        val CompletedBackground = rgb(245, 245, 245) // #F5F5F5
        val CompletedText = rgb(117, 117, 117) // #757575
        val CancelledBackground = rgb(255, 235, 238) // #FFEBEE
        val CancelledText = rgb(211, 47, 47) // #D32F2F
        val PayingOutBackground = rgb(255, 243, 224) // #FFF3E0
        val PayingOutText = rgb(245, 124, 0) // #F57C00
    }

    // Bracket Progress Colors
    object BracketProgress {
        val Complete = rgb(76, 175, 80) // #4CAF50
        val Partial = rgb(255, 193, 7) // #FFC107
        val Empty = rgb(158, 158, 158) // #9E9E9E
    }
}
